import { ContentType } from '../../components/plugins/chat/types';

export type ChatInformation = {
    agentsAvailable: boolean;
    authToken: null | string;
    chatableNow: boolean;
    chatId: null | string;
    chatSessionId: null | string;
    endUserNameForChat: string;
    hostUrl: string;
    initiated: boolean;
    lastMessageTime: null | number;
    popupConfig: Record<any, any>;
    preferences: null | Record<any, any>;
    setEndUserNameForChat: (fullName: string) => void;
    showNewChatForm: boolean;
    showSurvey: boolean;
    tenantId: null | string;
    userId: null | number;
};

export type ChatInputProps = {
    placeHolder: string;
};
export type KeysMatching<T, V> = {
    [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];
export interface IBasicMessage {
    id: string;
    body: {
        id?: number;
        sender: string;
        text: string;
        sentAt: any;
        status?: string;
        seenAt?: number;
        receiptId?: string;
    };
}
export type WsMessage = {
    contentType: ContentType;
    fromHistory?: boolean; // this is added to messages when they're coming from history end point
    id: string; // this is the msgGrispiId
    isSystem?: boolean; // true if welcome or dispatch message
    msgGrispiId?: string; // this exists in messages from history or websocket. we use this value as the id property in UI.
    receiptId?: string; // this is added to outgoing messages and also exists in incoming messages that come from ws subscription
    receivedAt?: number;
    seen: boolean;
    seenAt: number | undefined;
    sender: string;
    senderId: number;
    sentAt: number;
    status: string | undefined;
    text: string;
};

export type MessageBoxContent = {
    position: 'right' | 'left';
    type: 'text' | 'photo' | 'file' | 'location' | 'spotify' | 'video' | 'audio';
    text: string;
    date: number;
    title: string;
    status: 'waiting' | 'sent' | 'received' | 'read';
    notch: boolean;
    avatar: string;
};

export type PreferencesType = {
    text: {
        title: string;
        intro: string;
        welcome: string;
        dispatch: string;
        offline: string;
        prompt: string;
    };
    style: string;
    form: string;
    idleTimeout: number;
    systemName: string;
};

export type SubscribableChatResponseForEndUser = {
    chatId: string;
    chatSessionId: string;
    email: string;
    name: string;
    token: string;
    userId: string;
};

export type IFormData = {
    chatSessionId: string;
    token: string;
    brokerUrl: string;
    tenantId: string;
    endUserName: string;
};

/**
 * Message types between parent and this window
 */
export enum OutgoingMessageTypes {
    unseenMessageCount = 'grispi.chat.request.unseenMessageCount',
    setLastMessage = 'grispi.chat.request.lastMessageTime',
    ready = 'grispi.chat.request.ready',
    closePopup = 'grispi.chat.request.closePopup',
    newChatSession = 'grispi.chat.request.newChatSession'
}

/**
 * Events from chat.js to chat-popup (here)
 */
export enum IncomingMessageTypes {
    /**
     * This is an event coming from chat.js in response to our "ready" event
     */
    INIT = 'grispi.chat.response.init',
    POPUP_CLOSED = 'grispi.chat.event.popupClosed',
    POPUP_OPENED = 'grispi.chat.event.popupOpened',
    USER_WANTS_TO_END_CHAT = 'grispi.chat.event.userWantsToEndChat'
}

/**
 * Internal custom DOM event types
 */
export const InternalEventType = {
    CHAT_DISCONNECTED: 'chat-disconnected',
    CHAT_HISTORY_READY: 'CHAT_HISTORY_READY',
    CHAT_SESSION_CLOSED: 'CHAT_SESSION_CLOSED',
    CONNECTION_READY: 'CONNECTION_READY',
    CONNECTION_LOST: 'CONNECTION_LOST',
    ENSURE_WS_SUBSCRIPTION: 'ENSURE_WS_SUBSCRIPTION',
    GOT_INFO_MESSAGE: 'GOT_INFO_MESSAGE',
    GOT_RECEIPT: 'GOT_RECEIPT',
    MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
    MESSAGE_SEEN: 'MESSAGE_SEEN',
    INCOMING_MESSAGE: 'INCOMING_MESSAGE',
    NEED_SCROLL_TO_BOTTOM: 'NEED_SCROLL_TO_BOTTOM',
    NEED_TO_FOCUS_INPUT: 'NEED_TO_FOCUS_INPUT',
    NEW_CHAT_CREATED: 'NEW_CHAT_CREATED',
    RESUME_CHAT: 'RESUME_CHAT',
    /**
     * Dispatched when chat ws subscription is successful
     */
    SUBSCRIBED_TO_CHAT: 'SUBSCRIBED_TO_CHAT',
    /**
     * This event is dispatched when a chatSessionId is become available which is either after create new chat or after resume chat.
     */
    SUBSCRIBE_TO_CHAT: 'SUBSCRIBE_TO_CHAT',
    SURVEY_DONE: 'SURVEY_DONE',
    WINDOW_BLURRED: 'popup-blured',
    WINDOW_FOCUSED: 'popup-focused',

    /**
     * GRISPI NEWS
     */
    GRISPI_NEWS_TICKET_UPDATED: 'GRISPI_NEWS_TICKET_UPDATED',
    GRISPI_NEWS_TICKET_DELETED: 'GRISPI_NEWS_TICKET_DELETED'
} as const;

export enum SERVER_MESSAGE_CODE {
    CHAT_SESSION_CLOSED = 'CHAT_SESSION_CLOSED',
    INVALID_CHAT_SESSION = 'INVALID_CHAT_SESSION',
    INVALID_MESSAGE = 'INVALID_MESSAGE',
    MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
    MESSAGE_SEEN = 'MESSAGE_SEEN',
    NOT_AUTHORIZED = 'NOT_AUTHORIZED'
}

export const windowFocusedEvent = () => {
    window.dispatchEvent(new CustomEvent(InternalEventType.WINDOW_FOCUSED));
};
export const windowBlurredEvent = () => {
    window.dispatchEvent(new CustomEvent(InternalEventType.WINDOW_BLURRED));
};

export const websocketConnectionReadyEvent = () =>
    window.dispatchEvent(new CustomEvent(InternalEventType.CONNECTION_READY));
